import React, { FC, useCallback, useEffect } from "react";
import { useSelector } from "react-redux";
import { ServiceInfoDc } from "@evergis/api";

import { ProxyServiceReadOnlyMeta } from "./styled";
import { RestrictedWidthContainer } from "../../DialogComponents/styled";
import { AddProxyServiceWizardStep } from "./AddProxyServiceWizardStep";
import { ResourceMeta } from "../../DialogComponents/ResourceMeta/ResourceMeta";
import { ImportErrors } from "../../Import/components/ImportErrors";
import { ImportProgressBlock } from "../../Import/components/ProgressBlock";
import { layerWithTable } from "../../../../resources/layerWithTable";
import { isLocationMainSelector } from "../../../../ducks/location/selectors";
import { useAddLayersToCurrentProject } from "../../../../hooks/project/useAddLayersToCurrentProject";
import { useDialogReset } from "../../../../hooks/useDialogs";
import { useCreateTms } from "../../AddTmsService/hooks/useCreateTms";
import { useImportCancel } from "../../Import/hooks/useImportCancel";
import { SUBDOMAINS_SEPARATOR, useAddProxyServiceContext } from "./AddProxyServiceProvider";
import { useDefaultScaleRange } from "../../../../hooks/style/useDefaultScaleRange";

export const AddProxyServiceFinish: FC = () => {
  const { meta, sourceUrl, serviceType, layers, subDomainsValue } = useAddProxyServiceContext();
  const defaultScaleRange = useDefaultScaleRange();

  const { createTms, tmsResponse, tmsError } = useCreateTms({
    meta,
    sourceType: serviceType,
    sourceUrl,
    layers,
    subDomains: subDomainsValue.replaceAll(" ", "").split(SUBDOMAINS_SEPARATOR),
  });

  const cancelDialog = useDialogReset();
  const addLayersToCurrentProject = useAddLayersToCurrentProject();
  const cancelImport = useImportCancel({
    importInProgress: tmsResponse.loading,
    importDone: !tmsResponse.loading,
    targetLayerName: tmsResponse.data?.name,
  });
  const isProjectLocation = useSelector(isLocationMainSelector);
  const onFinish = useCallback(() => {
    isProjectLocation && tmsResponse.data && addLayersToCurrentProject([tmsResponse.data as ServiceInfoDc]);
    layerWithTable.invalidate({ name: tmsResponse.data?.name, defaultScaleRange });
    cancelDialog();
  }, [isProjectLocation, tmsResponse.data, addLayersToCurrentProject, defaultScaleRange, cancelDialog]);

  useEffect(() => {
    createTms();
  }, []);

  return (
    <AddProxyServiceWizardStep
      onPreviousStep={cancelImport}
      onCancel={cancelImport}
      nextStepDisabled={tmsResponse.loading}
      onNextStep={onFinish}
    >
      <RestrictedWidthContainer>
        <ProxyServiceReadOnlyMeta>
          <ResourceMeta meta={meta} readOnly showCopyrightAndDescription />
        </ProxyServiceReadOnlyMeta>
        <ImportProgressBlock layerCreating={tmsResponse.loading} layerCreated={!tmsResponse.loading} />
        {!!tmsError && <ImportErrors error={tmsError} importInProgress={tmsResponse.loading} />}
      </RestrictedWidthContainer>
    </AddProxyServiceWizardStep>
  );
};
